/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import useStyles from "./styles";
import { Grid } from "@mui/material";
import CardCount from "../../components/CardCount";
import { Group as StudentIcon, Folder, CalendarToday } from "@mui/icons-material";
import PageTitle from "../../components/PageTitle";
import WidgetTitle from "./components/WidgetTitle/WidgetTitle";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../components/Table/Table";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import { useUserState } from "../../context/UserContext";
import useWindowSize from "../../hooks/useWindow";
import Quote from "inspirational-quotes";
import ClassPeriod from "./components/ClassPeriodCard";
import NoData from "./components/NotFoundCard/NoData";
import NoNotice from "../../../src/assets/images/nofeatureicons/nonotice.svg";
import Modal from "../../components/Modal/index";
import InputGroup from "../../components/FormComponent/InputGroup";
import Button from "../../components/Button";
import NoticeBoardCard from "./components/NoticeBoardCard/NoticeBoardCard";
import PracticeSection from "./components/PracticeSection";

const SUBJECT_ENDPOINT = "/api/v1/student/subjects";
const ASSIGNMENT_ENDPOINT = "/api/v1/student/assignment";
const STUDENT_PERIOD = "/api/v1/period";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const STUDENT_OVERVIEW_ENDPOINT = "/api/v1/overview/student";

const StudentDashboard = () => {
  const { profile } = useUserState();
  const classes = useStyles();
  const [subjectCount, setSubjectCount] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [assignment, setAssignment] = useState([]);
  const [activeTerm, setActiveTerm] = useState([]);
  const [overview, setOverview] = useState([]);
  const [currentNotice, setCurrentNotice] = useState({});
  const [viewNoticeModal, setViewNoticeModal] = useState(false);
  const [quote, setQuote] = useState({ text: "", author: "" });

  const CLASS_ID = profile?.class_id;
  const width = useWindowSize();
  const mobile = width < 576;
  const navigate = useNavigate();

  /**
 * Determines the calendar view mode based on screen size.
 *
 * @function renderView
 * @returns {string} - Returns "timeGridDay" for mobile views, otherwise "timeGridWeek".
 */
  const renderView = () => {
    if (mobile) {
      return "timeGridDay";
    } else {
      return "timeGridWeek";
    }
  };

  /**
 * Fetches the list of subjects from the API and updates the subject count state.
 * Displays an error toast if the request fails.
 *
 * @function getSubject
 */
  const getSubject = () => {
    httprequest.get(`${SUBJECT_ENDPOINT}`)
      .then((res) => {
        setSubjectCount(res?.data?.data);
      }).catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches all academic terms from the API and sets the currently active term.
 * Displays an error toast if the request fails.
 *
 * @function getAllTerms
 */
  const getAllTerms = () => {
    httprequest.get(TERM_ENDPOINT).then((res) => {
      setActiveTerm(res?.data?.data.find((active_term) => active_term?.status === "active"));
    }).catch((err) => {
      toast.error(err?.response?.data);
    });
  };

  /**
 * Fetches all assignments from the API and updates the assignment state.
 * Displays an error toast if the request fails.
 *
 * @function getAssignments
 */
  const getAssignments = () => {
    httprequest.get(`${ASSIGNMENT_ENDPOINT}`)
      .then((res) => {
        setAssignment(res?.data?.data);
      }).catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches all periods for a specific class and updates the period data state.
 * Displays an error toast if the request fails.
 *
 * @function getAllPeriods
 */
  const getAllPeriods = () => {
    httprequest
      .get(`${STUDENT_PERIOD}?class_id=${CLASS_ID}`)
      .then((res) => {
        setPeriodData(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches student-related notifications or overview data and updates the state.
 * Silently fails if the request fails.
 *
 * @function getStudentOverview
 * @returns {void}
 */
  const getStudentOverview = () => {
    httprequest
      .get(STUDENT_OVERVIEW_ENDPOINT)
      .then((res) => {
        setOverview(res?.data.data || []);
      })
      .catch(() => {
      });
  };

  /**
 * Opens the notice modal and sets the currently selected notice to be viewed.
 *
 * @function handleViewNoticeModal
 * @param {Object} notice - The notice object to be displayed.
 */
  const handleViewNoticeModal = (notice) => {
    setCurrentNotice(notice);
    setViewNoticeModal(!viewNoticeModal);
  };

  /**
 * Closes the view notice modal by toggling its visibility.
 *
 * @function handleCloseViewNoticeModal
 */
  const handleCloseViewNoticeModal = () => {
    setViewNoticeModal(!viewNoticeModal);
  };

  /**
 * The start date of the current active term.
 *
 * @constant {Date} startDate
 */

  /**
 * The end date of the current active term.
 *
 * @constant {Date} EndDate
 */
  const startDate = new Date(activeTerm?.start_date);
  const EndDate = new Date(activeTerm?.end_date);

  /**
 * Generates calendar event objects based on the periods scheduled between the active term's start and end dates.
 * Matches weekdays with the period data's `days`, and constructs events accordingly.
 *
 * @function generateEvents
 * @returns {Array<Object>} - A list of calendar event objects.
 */

  /**
 * The list of events generated for rendering in the calendar.
 *
 * @constant {Array<Object>} events
 */

  /**
 * Renders the custom content for each calendar event.
 *
 * @function renderEventContent
 * @param {Object} eventInfo - The event information provided by the calendar.
 * @returns {JSX.Element} - JSX markup displaying the event title and teacher name.
 */
  const generateEvents = () => {
    const allEvents = [];
    const currentDate = new Date(startDate);

    while (currentDate <= EndDate) {
      const weekDay = currentDate
        // Get day and convert to lowercase abbreviat
        .toLocaleDateString("en-US", { weekday: "short" })
        .toLowerCase();

      periodData?.data?.days.forEach((day) => {
        if (weekDay === day?.day) {
          day?.periods.forEach((periodItem) => {
            const event = {
              type: periodItem?.type,
              title: periodItem?.subject?.name || periodItem?.title,
              start: `${currentDate.toISOString().split("T")[0]}T${periodItem?.start_time}`,
              end: `${currentDate.toISOString().split("T")[0]}T${periodItem?.end_time}`,
              teacher: periodItem?.teachers?.[0]?.name,
              subject_name: periodItem?.subject?.name,
              subject_id: periodItem?.subject?.id,
              day: day?.day,
              class: periodData?.data?.class?.name,
              class_id: periodData?.data?.class?.id,
              term: periodData?.data?.term?.name,
              term_id: periodData?.data?.term?.id,
              period_id: periodItem?.id,
              className: "general-events"
            };
            allEvents.push(event);
          });
        }
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allEvents;
  };

  const events = generateEvents();

  const renderEventContent = (eventInfo) => {
    return (
      <>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.title}</p>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.extendedProps?.teacher}</p>
      </>
    );
  };

  useEffect(() => {
    getSubject();
    getAssignments();
    getAllTerms();
    getAllPeriods();
    getStudentOverview();
    const fetchedQuote = Quote.getQuote();
    setQuote(fetchedQuote);
  }, []);

  /**
 * useEffect hook that re-fetches period data when CLASS_ID is available.
 *
 * @function useEffect (on CLASS_ID change)
 * @returns {void}
 */
  useEffect(() => {
    if (CLASS_ID) {
      getAllPeriods(CLASS_ID);
    }
  }, []);

  /**
 * Configuration array for columns in the assignments data table.
 *
 * @constant {Array<Object>} assignmentsColumns
 * @property {string} name - Column header name.
 * @property {function} selector - Function to extract the corresponding data from a row.
 */
  const assignmentsColumns = [
    {
      name: "Subject",
      selector: row => row?.subject?.name
    },
    {
      name: "Instruction",
      selector: row => row?.instruction || "--------"
    },
    {
      name: "Questions",
      selector: row => row?.questions?.length
    },
    {
      name: "Total Score",
      selector: row => row?.total_grade
    },
    {
      name: "Submission Date",
      selector: row => row?.submission_date?.substring(0, 10)
    }
  ];

  return (
    <Layout
      children={
        <>
          <PageTitle
            title={`Hello ${profile?.last_name} ${profile?.first_name},`}
            subtitle={
              <div className={classes.quoteContainer}>
                <h6 className={classes.quoteText}>{quote.text}</h6>
                <h5 className={classes.quoteAuthor}> - {quote.author}</h5>
              </div>
            }
            button={
              profile?.branding?.website_url ? (
                <Button
                  variant="primary"
                  buttonSize="fluid"
                  color="btndefault"
                  onClick={() => window.open(profile?.branding?.website_url, "_blank")}
                  buttonName="Visit school website"
                />
              ) : null
            }
          />
          <Grid container spacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Subject"
                count={subjectCount.length}
                icon={<StudentIcon />}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Learning Materials"
                count={0}
                icon={<Folder />}
              />
            </Grid>
          </Grid>
          {/* <WidgetTitle
            title="My TimeTable"
            subtitle=""
            // link={<Link className={classes.widgetlink} to="/teachers"><CalendarToday /> See full schedule</Link>}
          /> */}

          {/* <div className={classes.calendar}>
            <Calendar events={events} renderEventContent={renderEventContent} defaultView={renderView()} />
          </div> */}

          <WidgetTitle
            title="My Assignments"
            link={<Link style={{ fontSize: "14px", color: "black", fontWeight: "400" }} className={classes.widgetlink} to="/assesments/assignments">View all assignments</Link>}
          />
          <Table data={assignment} columns={assignmentsColumns} />
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <WidgetTitle
                title="Today's Classes"
                subtitle="View your schedule for the day"
              />
              <div className={classes.period}>
                <div className={classes.static}>
                  <h3 style={{ fontSize: "14px" }} onClick={() => navigate("/class-activities/classes/class-calendar")}>View all</h3>
                  <hr />
                </div>
                <div className={classes.periodclasses}>
                  <div className={classes.preriodflex}>
                    {overview?.period?.length > 0 ? (
                      overview?.period.map((period, index) => (
                        <div key={index} className={classes.perioditem}>
                          <ClassPeriod
                            start_time={period?.start_time}
                            end_time={period?.end_time}
                            period_subject={period?.period_subject}
                            period_class={period?.period_class}
                          />
                        </div>
                      ))
                    ) : (
                      <div className={classes.notfound}>
                        <NoData
                          img={NoNotice}
                          title="Empty Period"
                          subtitle="No periods available"
                        />
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
          <div style={{ marginTop: "20px" }}>
            <PracticeSection viewAll />
          </div>
          <Grid container spacing={2}>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <WidgetTitle
                title="Notice Board"
                subtitle="View important information for your school"
              />
              <div className={classes.notice}>
                <div className={classes.static}>
                  <h3 style={{ fontSize: "13px" }} onClick={() => navigate("/student/notice-board")}>View all</h3>
                  <hr />
                </div>
                <div className={classes.pendingleaves}>
                  {overview?.notice_board?.length > 0 ? (
                    overview.notice_board.map((notice) => (
                      <div key={notice.id}>
                        <NoticeBoardCard
                          viewClick={() => handleViewNoticeModal(notice)}
                          title={notice.title}
                          date={notice.created_at}
                          teacher={notice.added_by?.name}
                          priority={notice.priority}
                        />
                      </div>
                    ))
                  ) : (
                    <div className={classes.notfound}>
                      <NoData
                        img={NoNotice}
                        title="Empty Notice Board"
                        subtitle="No Information added"
                      />
                    </div>
                  )}

                </div>
              </div>
            </Grid>
          </Grid>
          <Modal
            title="View a Notice"
            subtitle={
              <div>
                View a notice set from your school
              </div>
            }
            modalContent={
              <>
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {currentNotice?.image?.file_url && (
                      <div
                        className={classes.viewBanner}
                        style={{
                          backgroundImage: `url(${currentNotice?.image?.file_url})`,
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat"
                        }}
                      >
                      </div>
                    )}
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Title"
                      required
                      disabled="disabled"
                      margin="normal"
                      placeHolder="Add Title of Document"
                      inputType="text"
                      inputName="title"
                      // onChange={handleEditChange}
                      defaultValue={currentNotice?.title}
                    />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="End Date"
                      margin="normal"
                      disabled="disabled"
                      // onChange={handleEditChange}
                      inputType="text"
                      required
                      value={currentNotice?.end_date}
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Description"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice Description"
                      inputType="text"
                      inputName="description"
                      // onChange={handleEditChange}
                      defaultValue={currentNotice?.description}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Priority"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice priority"
                      inputType="text"
                      inputName="priority"
                      // onChange={handleEditChange}
                      defaultValue={currentNotice?.priority}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Status"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice status"
                      inputType="text"
                      inputName="Status"
                      // onChange={handleEditChange}
                      defaultValue={currentNotice?.status}
                    />
                  </Grid>
                </Grid>
              </>
            }
            modalFooter={
              <>
                <div className="flex gap-10">
                  <Button
                    variant="primaryoutline"
                    buttonName="Cancel"
                    buttonSize="full"
                    color="btnfontprimary"
                    onClick={handleCloseViewNoticeModal}
                  />
                </div>
              </>
            }
            isopen={viewNoticeModal}
            closeModal={handleCloseViewNoticeModal}
          />
        </>
      }
    />
  );
};

export default StudentDashboard;
